import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Sparkles, Compass, Wand2, GitCompareArrows, Shuffle, Wine, Menu, X } from "lucide-react";
import BenoLogo from "./BenoLogo";

const navItems = [
  { to: "/gin/match", label: "Match", icon: Wand2, testid: "nav-match" },
  { to: "/gin/explore", label: "Explore", icon: Compass, testid: "nav-explore" },
  { to: "/gin", label: "Atlas", icon: Sparkles, testid: "nav-atlas" },
  { to: "/gin/flights", label: "Flights", icon: Wine, testid: "nav-flights" },
  { to: "/gin/compare", label: "Compare", icon: GitCompareArrows, testid: "nav-compare" },
  { to: "/gin/surprise", label: "Surprise", icon: Shuffle, testid: "nav-surprise" },
];

export default function Layout({ children }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <div className="App min-h-screen relative">
      {/* Top header */}
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
          scrolled ? "backdrop-blur-xl bg-black/60 border-b border-white/5" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <BenoLogo size={36} />
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.4em] text-[#C17F59] border-l border-[#C17F59]/40 pl-3">
              Gin&nbsp;Atlas
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/gin"}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `text-xs uppercase tracking-[0.25em] px-4 py-2 transition-colors ${
                    isActive ? "text-[#C17F59]" : "text-white/70 hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          {/* Mobile menu trigger */}
          <button
            className="md:hidden text-white/80 hover:text-[#C17F59] transition-colors"
            onClick={() => setOpen((v) => !v)}
            aria-label="menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile menu sheet */}
        {open && (
          <div className="md:hidden border-t border-white/5 bg-black/85 backdrop-blur-xl">
            <div className="px-5 py-4 grid gap-1">
              {navItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/gin"}
                  data-testid={`${n.testid}-mobile`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 text-sm uppercase tracking-[0.25em] ${
                      isActive ? "text-[#C17F59]" : "text-white/80"
                    }`
                  }
                >
                  <n.icon size={16} />
                  {n.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="pt-20 md:pt-24">
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 backdrop-blur-xl bg-black/80 border-t border-white/8"
        data-testid="bottom-nav"
      >
        <div className="grid grid-cols-6">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/gin"}
              data-testid={`${n.testid}-bottom`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-3 text-[8px] uppercase tracking-[0.16em] ${
                  isActive ? "text-[#C17F59]" : "text-white/50"
                }`
              }
            >
              <n.icon size={16} strokeWidth={1.4} />
              {n.label}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Footer */}
      <footer className="border-t border-white/5 mt-24 px-6 md:px-12 py-12 text-center text-xs text-white/40 tracking-[0.3em] uppercase">
        <p>BENO · India's Largest Gin Menu, Now Explorable</p>
        <Link
          to="/admin/login"
          className="inline-block mt-4 text-[9px] tracking-[0.4em] text-white/20 hover:text-[#C17F59] transition-colors"
          data-testid="footer-control-room"
          title="Staff only"
        >
          · BENO Control Room ·
        </Link>
      </footer>
    </div>
  );
}
